import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModuleAccess } from '../contexts/ModuleAccessContext';
import { useRolePermissions } from '../contexts/RolePermissionsContext';
import { Button } from './ui/button';
import { useOptimizedPolling } from '../hooks/useOptimizedPolling';
import { safeFetchAuth } from '../utils/safeFetch';
import { LogOut, User, Users, Shield, Car, BarChart3, DollarSign, Settings, FileText, FileUp, Smartphone, UserCog, X, Wrench, Home, ChevronDown, TrendingUp, RefreshCw, Calendar, ClipboardCheck, Bell, Wallet, Building2, Facebook, Zap, Mail, MessageSquare, LayoutGrid, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import NotificationCenter from './NotificationCenter';
import AgendaModal from './AgendaModal';
import api from '../utils/axiosClient'; // Centralized API client with auto-auth

const AppHeader = ({ 
  onOpenObjectives, 
  onOpenUserManagement, 
  onOpenReportsConfig, 
  onOpenExcelUpload 
}) => {
  const { user, realUser, isImpersonating, impersonateUser, exitImpersonation, logout, isReady: authReady } = useAuth();
  const { hasAccess } = useModuleAccess();
  const { canViewPage, canEditPage } = useRolePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadSMSCount, setUnreadSMSCount] = useState(0);

  const isManager = user?.role === 'manager';

  // Use window.location.origin for production to avoid stale env variable issues
  const backendUrl = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
    ? window.location.origin 
    : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

  // Fetch unread SMS count - filtered by analyst for non-admin users
  const fetchUnreadSMS = useCallback(async () => {
    try {
      // For admins, show all unread. For analysts/advisors, filter to their assigned leads
      const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
      const filterParam = isAdminUser ? '' : '?filter_by_analyst=true';
      
      const result = await safeFetchAuth(`${backendUrl}/api/sms/conversations${filterParam}`);
      if (result.ok && result.data) {
        const conversations = result.data.conversations || result.data || [];
        const unreadConversations = conversations.filter(c => c.unread_count > 0);
        setUnreadSMSCount(unreadConversations.length);
      }
    } catch (error) {
      console.error('Error fetching SMS count:', error);
    }
  }, [backendUrl, user?.role]);

  // Poll for unread SMS
  useOptimizedPolling(fetchUnreadSMS, 60000, {
    enabled: true,
    storageKey: 'sms-unread-count',
    immediate: false
  });

  // Initial SMS fetch
  useEffect(() => {
    const timer = setTimeout(fetchUnreadSMS, 1000);
    return () => clearTimeout(timer);
  }, [fetchUnreadSMS]);

  // Fetch notifications with optimized polling
  const fetchNotifications = useCallback(async () => {
    try {
      const [countResult, notifResult] = await Promise.all([
        safeFetchAuth(`${backendUrl}/api/notifications/unread-count`),
        safeFetchAuth(`${backendUrl}/api/notifications`)
      ]);
      
      if (countResult.ok && countResult.data) {
        setUnreadCount(countResult.data.count);
      }
      
      if (notifResult.ok && notifResult.data) {
        setNotifications(notifResult.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [backendUrl]); // Only depend on backendUrl, not state
  
  // Use optimized polling - stops when tab hidden, shares data across tabs
  useOptimizedPolling(fetchNotifications, 30000, {
    enabled: true,
    storageKey: 'app-notifications',
    immediate: false  // Don't fire immediately - let component settle first
  });
  
  // Initial fetch after mount
  useEffect(() => {
    const timer = setTimeout(fetchNotifications, 500); // Delay initial fetch
    return () => clearTimeout(timer);
  }, [fetchNotifications]);

  // Fetch all users if admin or super_admin - Uses centralized API client
  useEffect(() => {
    const fetchUsers = async () => {
      // Gate: Only fetch when auth is ready (for UI purposes, not for headers)
      if (!authReady) {
        return;
      }
      
      if (realUser?.role === 'admin' || realUser?.role === 'super_admin') {
        try {
          setLoadingUsers(true);
          // Use centralized api client - Authorization header is added automatically
          const response = await api.get('/api/users');
          // Filter out sales reps - they have no access
          const filteredUsers = response.data.filter(u => u.role !== 'sales_rep');
          setAllUsers(filteredUsers);
        } catch (error) {
          // Handle 403 gracefully - user doesn't have permission
          if (error.isPermissionError || error.response?.status === 403) {
            console.debug('[AppHeader] No permission to fetch users - hiding user list');
            setAllUsers([]);
          } else if (error.isAuthError) {
            // Auth error handled by interceptor, just clear list
            setAllUsers([]);
          } else {
            console.error('[AppHeader] Error fetching users:', error.message);
            setAllUsers([]);
          }
        } finally {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();
  }, [realUser, authReady]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleImpersonate = (userId) => {
    const userToImpersonate = allUsers.find(u => u.id === userId);
    if (userToImpersonate) {
      impersonateUser(userToImpersonate);
      // Navigate to appropriate dashboard
      if (userToImpersonate.role === 'sales_rep') {
        navigate('/sales-rep-dashboard');
      } else if (userToImpersonate.role === 'team_manager') {
        navigate('/team-dashboard');
      } else if (userToImpersonate.role === 'manager') {
        navigate('/manager');
      } else {
        navigate('/home');
      }
    }
  };

  const handleExitImpersonation = () => {
    exitImpersonation();
    navigate('/home');
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Administrateur',
      manager: 'Manager',
      team_manager: 'Manager d\'Équipe',
      sales_rep: 'Représentant',
      accountant: 'Comptable',
      advisor: 'Conseiller',
      user: 'Utilisateur'
    };
    return labels[role] || role;
  };

  // Check if user is admin (includes super_admin)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Check if user is admin/manager who should see extra buttons
  const isAdminOrManager = isAdmin || user?.role === 'manager';
  
  // Check if user has "user" role - limited view (inventory, wholesale, sales only)
  const isUserRole = user?.role === 'user';
  
  // Check if user is super admin - check user context AND localStorage
  const checkSuperAdmin = () => {
    if (user?.role === 'super_admin') return true;
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.role === 'super_admin';
      }
    } catch (e) {}
    return false;
  };
  const isSuperAdmin = checkSuperAdmin();
  
  // Check if user is accountant - restricted view
  const isAccountant = user?.role === 'accountant';
  
  // Check if user is manager - restricted view (only Ventes, Inventaire, CRM, Evaluations, Depot)
  const isManagerRestricted = user?.role === 'manager';
  
  // Check if user is marketing-only (can only see CRM/Marketing page)
  const MARKETING_ONLY_USERS = ['maxime@glive.ca'];
  const isMarketingOnly = MARKETING_ONLY_USERS.includes(user?.email?.toLowerCase());
  
  // Check if on CRM-related pages (simplified nav)
  const isOnCRMPage = location.pathname === '/marketing' || location.pathname === '/today';

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCog className="h-4 w-4" />
            <span className="text-sm font-medium">
              Viewing as: {user?.name} ({getRoleLabel(user?.role)})
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleExitImpersonation}
            className="text-white hover:bg-amber-600 h-7 gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </Button>
        </div>
      )}

      {/* Mobile Mini Header - Only essential elements */}
      <header className="lg:hidden flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-950">
        <img 
          src="/logo-autohub360.png" 
          alt="AutoHub360" 
          className="h-8 w-auto object-contain cursor-pointer"
          onClick={() => navigate('/home')}
        />
        <div className="flex items-center gap-2">
          {/* Notifications - hidden for managers */}
          {!isManager && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-zinc-800"
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
          )}
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-zinc-800">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-700">
              <DropdownMenuLabel className="text-zinc-300 text-xs">
                {user?.name || 'Utilisateur'}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-700" />
              <DropdownMenuItem onClick={() => navigate('/settings')} className="text-zinc-300 hover:bg-zinc-800 cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                Paramètres
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="text-red-400 hover:bg-zinc-800 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Header - Full navigation */}
      <header className="hidden lg:block border-b border-white/10 px-2 md:px-6 py-1 md:py-3" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="flex justify-between items-center gap-1 md:gap-2">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img 
              src="/logo-autohub360.png" 
              alt="AutoHub360" 
              className="h-12 w-auto md:h-16 lg:h-16 object-contain cursor-pointer"
              onClick={() => navigate('/home')}
            />
          </div>

        {/* Navigation Buttons - Hidden on mobile, visible on desktop */}
        <div className="hidden lg:flex items-center gap-1 flex-wrap justify-center flex-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="bg-gray-700 hover:bg-gray-600 h-7 w-7 p-0 border-gray-600 text-gray-200"
            title="Actualiser la page"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
          
          {/* Home button - goes to launcher */}
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/home')}
            className="gap-0.5 bg-slate-700 hover:bg-slate-600 h-7 text-[11px] px-1.5 text-gray-100"
            title="Accueil"
          >
            <Home className="h-3 w-3" />
          </Button>
          
          {/* User role - dynamic navigation based on permissions */}
          {isMarketingOnly ? (
            /* Marketing Only users - can see CRM, KPIs, and Lead Flow Board */
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/marketing')}
                className="gap-0.5 bg-blue-600 hover:bg-blue-700 h-7 text-[11px] px-1.5 text-white"
              >
                <Users className="h-3 w-3" />
                <span>CRM</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/kpi-dashboard')}
                className="gap-0.5 bg-purple-600 hover:bg-purple-700 h-7 text-[11px] px-1.5 text-white"
              >
                <BarChart3 className="h-3 w-3" />
                <span>KPIs</span>
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/lead-flow-board')}
                className="gap-0.5 bg-green-600 hover:bg-green-700 h-7 text-[11px] px-1.5 text-white"
              >
                <LayoutGrid className="h-3 w-3" />
                <span>Flow Board</span>
              </Button>
            </>
          ) : (
            <>
          {/* REMOVED: President Dashboard - not needed for corner dealers */}
          
          {/* Ventes */}
          {hasAccess('sales') && canViewPage('sales') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/sales-dashboard')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <BarChart3 className="h-3 w-3" />
            <span>Ventes</span>
          </Button>
          )}
          
          {/* Inventory */}
          {hasAccess('inventory') && canViewPage('inventory') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/inventory')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <Car className="h-3 w-3" />
            <span>Invent.</span>
          </Button>
          )}
          
          {/* CRM */}
          {hasAccess('crm') && canViewPage('crm') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/marketing')}
              className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
            >
              <Users className="h-3 w-3" />
              <span>CRM</span>
            </Button>
          )}

          {/* Appraisals */}
          {hasAccess('evaluations') && canViewPage('evaluations') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/appraisals')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <ClipboardCheck className="h-3 w-3" />
            <span>Éval.</span>
          </Button>
          )}
          
          {/* Wholesale */}
          {hasAccess('wholesale') && canViewPage('wholesale') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/wholesale-contracts')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <FileText className="h-3 w-3" />
            <span>Wholesale</span>
          </Button>
          )}
          
          {/* Comptabilité */}
          {canViewPage('accounting') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/accounting')}
            className="gap-0.5 bg-emerald-700 hover:bg-emerald-600 h-7 text-[11px] px-1.5 text-white"
          >
            <FileText className="h-3 w-3" />
            <span>Compta.</span>
          </Button>
          )}
          
          {/* Dépôts */}
          {canViewPage('depots') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/deposits')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <DollarSign className="h-3 w-3" />
            <span>Dépôts</span>
          </Button>
          )}
          
          {/* REMOVED: Trésorerie (Cashflow) - not needed for corner dealers */}

          {/* Removed: Nitin Commissions link - now integrated in sales dashboard */}

          {/* Recon Page */}
          {hasAccess('reconditioning') && canViewPage('recon') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/recon')}
            className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
          >
            <Wrench className="h-3 w-3" />
            <span>Recon.</span>
          </Button>
          )}
          
          {/* REMOVED: Floorplan - not needed for corner dealers */}
          
          {/* Supplier Payments */}
          {canViewPage('suppliers') && !isOnCRMPage && (
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/supplier-payments')}
            className="gap-0.5 bg-purple-600 hover:bg-purple-700 h-7 text-[11px] px-1.5 text-white"
            data-testid="nav-supplier-payments"
          >
            <DollarSign className="h-3 w-3" />
            <span>Fournisseurs</span>
          </Button>
          )}
          
          {/* Today - My Actions (Cadence) - Only visible on CRM page */}
          {hasAccess('crm') && canViewPage('today') && (location.pathname === '/marketing' || location.pathname === '/today') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/today')}
              className={`gap-0.5 h-7 text-[11px] px-1.5 ${
                location.pathname === '/today' 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-100'
              }`}
            >
              <Calendar className="h-3 w-3" />
              <span>Aujourd'hui</span>
            </Button>
          )}
          
          {/* Agenda Button - Hidden on CRM pages for cleaner nav */}

          {/* Sales Board */}
          {hasAccess('reports') && canViewPage('tableau') && !isOnCRMPage && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/sales-board')}
              className="gap-0.5 bg-gray-700 hover:bg-gray-600 h-7 text-[11px] px-1.5 text-gray-100"
            >
              🏆
              <span>Tableau</span>
            </Button>
          )}

          {/* REMOVED: Budget - not needed for corner dealers */}
          
          {/* Removed: Delivery Calendar Button - delivery module disabled */}
          
          {/* REMOVED: Dealer Management Button - not needed for corner dealers */}
          
          {/* FB Poster SaaS Dealers */}
          {canViewPage('dealers') && !isOnCRMPage && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/fb-poster-admin')}
              className="gap-0.5 bg-purple-600 hover:bg-purple-500 h-7 text-[11px] px-1.5 text-white"
              title="Manage FB Poster SaaS Dealers"
            >
              <Users className="h-3 w-3" />
              <span>FB Poster SaaS</span>
            </Button>
          )}
          
          {/* FB Marketplace Bot */}
          {isAdmin && !isManagerRestricted && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/fb-marketplace')}
              className="gap-0.5 bg-blue-600 hover:bg-blue-500 h-7 text-[11px] px-1.5 text-white"
              title="Facebook Marketplace"
            >
              <Facebook className="h-3 w-3" />
              <span>FB Marketplace</span>
            </Button>
          )}
          
          {/* Dealer Website Management */}
          {isAdmin && !isManagerRestricted && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/dealer-website')}
              className="gap-0.5 bg-emerald-600 hover:bg-emerald-500 h-7 text-[11px] px-1.5 text-white"
              title="Gestion du Site Web"
              data-testid="nav-dealer-website"
            >
              <Globe className="h-3 w-3" />
              <span>Site Web</span>
            </Button>
          )}
          
          {/* Removed: Marketing AI Agent button - marketing AI module disabled */}
          
          {/* Automations - Only on CRM page */}
          {canViewPage('automations') && isOnCRMPage && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/automations')}
              className="gap-0.5 bg-amber-600 hover:bg-amber-500 h-7 text-[11px] px-1.5 text-white"
              title="Automations"
            >
              <Zap className="h-3 w-3" />
              <span>Automations</span>
            </Button>
          )}
            </>
          )}
        </div>

        {/* Mobile Navigation - Icon only buttons with horizontal scroll */}
        <div className="flex lg:hidden items-center gap-1 flex-1 overflow-x-auto scrollbar-hide px-1">
          <div className="flex items-center gap-1 min-w-max">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 border-gray-600 text-gray-200"
              title="Actualiser la page"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            
            {/* Mobile navigation - dynamic based on permissions */}
            
            {/* REMOVED: President Dashboard Mobile - not needed for corner dealers */}
            
            {/* Ventes */}
            {hasAccess('sales') && canViewPage('sales') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/sales-dashboard')}
              className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
              title="Ventes"
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </Button>
            )}
            
            {/* Inventory */}
            {hasAccess('inventory') && canViewPage('inventory') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/inventory')}
              className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
              title="Inventaire"
            >
              <Car className="h-3.5 w-3.5" />
            </Button>
            )}
            
            {/* Wholesale */}
            {hasAccess('wholesale') && canViewPage('wholesale') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/wholesale-contracts')}
              className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
              title="Wholesale Contracts"
            >
              <FileText className="h-3.5 w-3.5" />
            </Button>
            )}
            
            {/* Comptabilité Mobile */}
            {canViewPage('accounting') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/accounting')}
              className="bg-emerald-700 hover:bg-emerald-600 h-8 w-8 p-0 flex-shrink-0 text-white"
              title="Comptabilité"
            >
              <FileText className="h-3.5 w-3.5" />
            </Button>
            )}
            
            {/* Dépôts Mobile - HIDDEN as per user request */}
            
            {/* REMOVED: Trésorerie Mobile - not needed for corner dealers */}
            
            {/* CRM */}
            {hasAccess('crm') && canViewPage('crm') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/marketing')}
                className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
                title="CRM"
              >
                <TrendingUp className="h-3.5 w-3.5" />
              </Button>
            )}
            
            {/* Agenda Mobile Button - Only visible on CRM page */}
            {hasAccess('crm') && canViewPage('crm') && location.pathname === '/marketing' && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAgenda(true)}
                className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
                title="Agenda"
              >
                <Calendar className="h-3.5 w-3.5" />
              </Button>
            )}
            
            {/* Sales Board Mobile */}
            {hasAccess('reports') && canViewPage('tableau') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/sales-board')}
                className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
                title="Tableau des Ventes"
              >
                🏆
              </Button>
            )}
            
            {/* REMOVED: Budget Mobile - not needed for corner dealers */}
            
            {/* Removed: Delivery Calendar Mobile Button - delivery module disabled */}

            {/* Removed: Nitin Commissions link - now integrated in sales dashboard */}

            {/* Recon Mobile */}
            {hasAccess('reconditioning') && canViewPage('recon') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/recon')}
                className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
                title="Reconditionnement"
              >
                <Wrench className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Appraisals Mobile */}
            {hasAccess('evaluations') && canViewPage('evaluations') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/appraisals')}
                className="bg-gray-700 hover:bg-gray-600 h-8 w-8 p-0 flex-shrink-0 text-gray-100"
                title="Évaluations"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
              </Button>
            )}
            
            {/* Automations Mobile Button - Only on CRM page */}
            {canViewPage('automations') && location.pathname === '/marketing' && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/automations')}
                className="bg-amber-600 hover:bg-amber-500 h-8 w-8 p-0 flex-shrink-0 text-white"
                title="Automations"
              >
                <Zap className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Admin Panel Buttons */}
            {canViewPage('settings') && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenObjectives}
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="Objectifs"
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenUserManagement}
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="Conseillers"
                >
                  <Users className="h-3.5 w-3.5" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenReportsConfig}
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="Rapports"
                >
                  <FileText className="h-3.5 w-3.5" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenExcelUpload}
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="Import"
                >
                  <FileUp className="h-3.5 w-3.5" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/debug'}
                  className="h-8 w-8 p-0 flex-shrink-0 bg-red-50 hover:bg-red-100 border-red-300"
                  title="Debug Dashboard (Admin)"
                >
                  <svg className="h-3.5 w-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Right side - User Impersonation (Admin Only), Notifications and User Menu */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* User Impersonation Selector */}
          {canEditPage('users') && !isImpersonating && (
            <div className="hidden lg:block">
              <Select onValueChange={handleImpersonate} disabled={loadingUsers}>
                <SelectTrigger className="w-[120px] h-7 text-[11px] border-slate-300">
                  <div className="flex items-center gap-1">
                    <UserCog className="h-3 w-3" />
                    <SelectValue placeholder="View as..." />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-xs">
                      {u.name} - {getRoleLabel(u.role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Messages Button with Badge */}
          <Button
            variant="outline"
            onClick={() => navigate('/messages')}
            className="relative flex items-center gap-1 text-[11px] px-1.5 h-7 border-gray-600 bg-gray-700 hover:bg-gray-600 text-white"
            title="Messages"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Messages</span>
            {unreadSMSCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-teal-500 rounded-full border-2 border-gray-800 animate-pulse">
                {unreadSMSCount > 99 ? '99+' : unreadSMSCount}
              </span>
            )}
          </Button>

          <NotificationCenter 
            notifications={notifications} 
            unreadCount={unreadCount}
            onRefresh={fetchNotifications}
            onCountChange={setUnreadCount}
          />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-1 text-[11px] px-1.5 h-7 border-gray-600 bg-gray-700 hover:bg-gray-600 text-white">
                <User className="h-3 w-3" />
                <span className="hidden lg:inline">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  {user?.title && (
                    <p className="text-xs text-gray-600 font-normal italic">{user.title}</p>
                  )}
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {getRoleLabel(user?.role)}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Admin Menu Section */}
              {canViewPage('settings') && (
                <>
                  <DropdownMenuLabel className="text-xs font-semibold text-slate-600">
                    Administration
                  </DropdownMenuLabel>
                  
                  {canViewPage('reports') && (
                  <DropdownMenuItem onClick={() => navigate('/reports')} className="text-sm cursor-pointer">
                    <FileText className="h-4 w-4 mr-2" />
                    Reports
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="text-sm cursor-pointer">
                    <Settings className="h-4 w-4 mr-2" />
                    Configuration
                  </DropdownMenuItem>
                  
                  {canEditPage('sales') && (
                  <DropdownMenuItem onClick={onOpenObjectives} className="text-sm cursor-pointer">
                    <Settings className="h-4 w-4 mr-2" />
                    Objectifs Mensuels
                  </DropdownMenuItem>
                  )}
                  
                  {canEditPage('users') && (
                  <DropdownMenuItem onClick={onOpenUserManagement} className="text-sm cursor-pointer">
                    <Users className="h-4 w-4 mr-2" />
                    Gestion Conseillers
                  </DropdownMenuItem>
                  )}
                  
                  {canViewPage('reports') && (
                  <DropdownMenuItem onClick={onOpenReportsConfig} className="text-sm cursor-pointer">
                    <FileText className="h-4 w-4 mr-2" />
                    Config Rapports
                  </DropdownMenuItem>
                  )}
                  
                  {canEditPage('crm') && (
                  <DropdownMenuItem onClick={onOpenExcelUpload} className="text-sm cursor-pointer">
                    <FileUp className="h-4 w-4 mr-2" />
                    Import Excel
                  </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem onClick={() => navigate('/gmail-sync')} className="text-sm cursor-pointer">
                    <Mail className="h-4 w-4 mr-2" />
                    Gmail Sync
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                </>
              )}
              
              <DropdownMenuItem onClick={() => window.open('/install.html', '_blank')}>
                <Smartphone className="h-4 w-4 mr-2" />
                Installer l'app mobile
              </DropdownMenuItem>
              {canViewPage('dealers') && (
                <DropdownMenuItem onClick={() => navigate('/dealers')} className="text-blue-600 font-medium">
                  <Building2 className="h-4 w-4 mr-2" />
                  Dealer Management
                </DropdownMenuItem>
              )}
              {canViewPage('users') && (
                <DropdownMenuItem onClick={() => navigate('/users')}>
                  <Shield className="h-4 w-4 mr-2" />
                  Gestion des utilisateurs
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    
    {/* Agenda Modal */}
    <AgendaModal 
      isOpen={showAgenda} 
      onClose={() => setShowAgenda(false)} 
    />
    </>
  );
};

export default AppHeader;
